import { html, css, nothing } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';
import { when } from 'lit/directives/when.js';
import { consume } from '@lit/context';
import { ContextChangeController } from '../../../ns-services/context-change.js';
import { nsStripeServiceContext, nsDirectoryStateContext } from '../../../ns-services/contexts.js';

import { nsLayoutStyles } from '../../../ns-styles/index.js';

import { NSStripeService } from '../../../ns-services/ns-stripe-service.js';

import { callLambda } from '../../../helpers/lambda.js';
import { lambdaRefusal } from '../../../helpers/public-ledger-balance-api.js';
import {
  COUNTRY_NAMES,
  billingPrefillFromCompany,
  billingProblemsFromMessage,
  draftFromBilling,
  normaliseBillingDetails,
  type BillingDetails,
  type BillingDraft,
} from '../../../helpers/billing-details.js';

import Company from '../../../models/company.js';
import JobSchema from '../../../models/job-schema.js';

import NSJobComponent from '../job.js';
import Subscription from '../../../models/subscription.js';

import { LiveAnnouncer } from '../../../helpers/live-announcer.js';
import '../../../ns-elements/ns-job-layout.js';
import '../../../ns-elements/ns-summary-card.js';
import '../../../ns-elements/ns-billing-details.js';
import type { NSBillingDetails } from '../../../ns-elements/ns-billing-details.js';
import { renderMoney } from '../../../helpers/format-render.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import definition from '@job-schemas/onboarding-payment.json';
import { jobSchema } from '../job-schema.js';

// The billing details a subscription row holds, as the form and payment/subscribe take them
function billingFromSubscription(subscription: Subscription): BillingDetails {
  const { customer } = subscription.billing;
  return normaliseBillingDetails({ email: customer.email, phone: customer.mobileNumber, address: customer.address });
}

@customElement(NSOnboardingPayment.is)
export default class NSOnboardingPayment extends NSJobComponent {
  static get is() {
    return 'ns-onboarding-payment';
  }

  private __companyChanges = new ContextChangeController(this, nsDirectoryStateContext, () => {
    this.__company = this._directory?.company;
  });

  @consume({ context: nsStripeServiceContext })
  private __stripe?: NSStripeService;

  @query('ns-billing-details')
  private __billingForm?: NSBillingDetails;

  // The company paying: its billing details prefill the form, and its label heads the summary
  @state()
  private __company?: Company;

  // The details sent to payment/subscribe (or saved on the subscription); the summary shows them
  @state()
  private __billing?: BillingDetails;

  // The form's starting text: the company's details, or the saved ones when the payer changes them
  @state()
  private __prefill: Partial<BillingDraft> | null = null;

  // Whether the payer went back to change details already sent
  @state()
  private __editing: boolean = false;

  @state()
  private __clientSecret?: string;

  @state()
  private __subscription?: Subscription;

  @state()
  private __succeeded?: boolean;

  private __announcer = new LiveAnnouncer(this);

  @state()
  private __customerConfirmed: boolean = false;

  @state()
  private __submitting: boolean = false;

  @state()
  private __selectedPlan?: 'MONTHLY' | 'YEARLY';

  @state()
  private __pricing?: { monthly: number; yearly: number } | null;

  static styles = [
    nsLayoutStyles,
    ...super.styles,
    css`
      .plan.selected {
        background: var(--ns-theme-green);
      }

      .plan .desc {
        font-size: var(--wa-font-size-xs);
        font-weight: 300;
      }

      .plan .fee {
        font-size: var(--wa-font-size-l);
      }

      .plan .fee span {
        font-size: var(--wa-font-size-xs);
      }

      .brand-icon {
        width: 3.5rem;
        height: 2rem;
        background-repeat: no-repeat;
        background-position: center;
        background-size: contain;
      }
    `,
  ];

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { complete: { type: 'hidden' } } });
  }

  async connectedCallback() {
    await super.connectedCallback();
    await this.updateComplete;

    if (!this._db) throw new Error('Unable to find db service');

    if (!this._auth) throw new Error('Auth service is required!');

    if (!this._directory) throw new Error('Unable to find NSDirectoryService');

    if (!this.__stripe) throw new Error('Unable to find stripe service');

    if (!this._job) throw new Error('No job available');

    const subscriptionMetadata = this._job.metadata.find((m) => m.key === 'SUBSCRIPTION_ID');
    if (subscriptionMetadata && subscriptionMetadata.value) {
      this.__subscription = await this._db.getById('subscription', subscriptionMetadata.value);
    }

    const paymentMetadata = this._job.metadata.find((m) => m.key === 'PAYMENT');
    if (paymentMetadata && paymentMetadata.value === 'PAID') {
      this.__succeeded = true;
      this.__announcer.announce('Payment successful.');
    }
    if (paymentMetadata && paymentMetadata.value !== 'PAID') {
      this.__succeeded = false;
      this.__announcer.announce('Payment was not completed.');
    }

    if (this.__subscription) {
      this._subscriptions.subscribe(`subscription.${this.__subscription?.id}.*`, () => {
        this.__subscription = this._db?.get(`subscription.${this.__subscription?.id}`);
      });

      this.__selectPaymentPlan(this.__subscription?.plan);
      this.__billing = billingFromSubscription(this.__subscription);
    }

    await this._directory.initialised;
    this.__company = this._directory.company;

    if (!this.__company) throw new Error('Could not find company linked to this user profile');

    // Nothing comes from the person paying: the billing details are the company's
    this.__prefill = billingPrefillFromCompany(this.__company);

    this.loading('Fetching tier pricing');
    try {
      const pricingResult = await callLambda(
        'payment/pricing',
        'GET',
        this._auth.token,
        undefined,
        `jobId=${this._job.id}`,
      );
      const { pricing } = pricingResult.res as unknown as {
        tier: string | null;
        pricing: { monthly: number; yearly: number } | null;
      };
      this.__pricing = pricing;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to fetch subscription pricing';
      this.triggerCardError(message);
    } finally {
      this.loaded();
    }
  }

  async disconnectedCallback() {
    this.__stripe?.cleanElements();

    super.disconnectedCallback();
  }

  // The label the company's subscription customer and invoices carry
  private get __companyLabel() {
    return `${this.__company?.name ?? 'Company'} (Subscription)`;
  }

  // Confirm on the form: shows every problem beside its field, or sends the typed details
  private async __confirmForm() {
    const form = this.__billingForm;
    if (!form || !form.reportValidity() || !form.billing) return;
    await this.__submitBilling(form.billing);
  }

  // Sends the billing details and the plan to payment/subscribe, which creates the company's Stripe customer or (when the
  // payer went back and changed them) updates it, then mounts Stripe's card form for the payment it starts
  private async __submitBilling(billing: BillingDetails) {
    if (!this._job || !this._job.id) throw new Error('Missing job info in the job card');
    if (this.__submitting) return;
    this.clearError();

    if (!this.__selectedPlan) {
      this.showError('Choose a plan before confirming the billing details.');
      return;
    }

    this.__submitting = true;
    this.loading('Creating a payment intent');
    try {
      if (!this._auth) throw new Error('Can not find auth service');
      if (!this.__stripe) throw new Error('Unable to find stripe service');

      const output = await callLambda(
        'payment/subscribe',
        'POST',
        this._auth.token,
        JSON.stringify({
          jobId: this._job.id,
          billing,
          plan: this.__selectedPlan,
          provider: 'STRIPE',
        }),
      );

      const res = output.res as unknown as { clientSecret: string };
      if (!res.clientSecret) throw new Error('Did not receive a client secret back from the lambda');

      this.__billing = billing;
      this.__editing = false;
      this.__clientSecret = res.clientSecret;
      await this.updateComplete;
      // The Stripe service is shared: a form left from an earlier confirm (or another card) would otherwise be reused
      this.__stripe.cleanElements();
      // Stripe's postcode and country start at the billing details just saved
      await this.__stripe.mountStripeElement(this, res.clientSecret, null, billing.address);

      this.__customerConfirmed = true;
    } catch (err) {
      await this.__showRefusal(lambdaRefusal(err, 'The billing details could not be saved. Please try again.'));
    } finally {
      this.loaded();
      this.__submitting = false;
    }
  }

  // A refusal that names billing fields shows beside them (opening the form when the summary was confirmed); any other
  // shows on the card
  private async __showRefusal(message: string) {
    if (!this.__billingForm && this.__billing && billingProblemsFromMessage(message).length > 0) {
      this.__changeDetails();
      await this.updateComplete;
    }
    if (this.__billingForm?.showServerProblems(message)) return;
    this.showError(message);
  }

  // Back to the form, starting from the details already sent, so a re-submit sends what the payer edits
  private __changeDetails() {
    this.clearError();
    this.__stripe?.cleanElements();
    this.__clientSecret = undefined;
    this.__customerConfirmed = false;
    this.__prefill = draftFromBilling(this.__billing);
    this.__editing = true;
  }

  private __cancelChange() {
    this.clearError();
    this.__editing = false;
  }

  private async __completePayment() {
    if (!this.__stripe) throw new Error('Unable to find stripe service');
    if (!this._job) throw new Error('Unable to find job');

    this.loading('Processing Payment');
    await this.__stripe.proceedPayment(this._job.id);
    this.loaded();
  }

  // The plan blocks render selected from __selectedPlan, so this works before they exist, as when
  // a saved subscription's plan is restored on load ahead of the pricing
  private __selectPaymentPlan(plan: string | undefined) {
    if (!this._job || !this._job.id) throw new Error('Missing job info in the job card');
    this.clearError();

    if (plan !== 'MONTHLY' && plan !== 'YEARLY') return;
    this.__selectedPlan = plan;
  }

  private __renderSubscriptionPlan() {
    return html`
      <div class="job-panel">
        <h3 id="subscription-plan-heading">Choose Your Subscription</h3>
        ${!this.__pricing
          ? html`
              <p>Pricing isn't available yet.</p>
            `
          : html`
              <div role="group" aria-labelledby="subscription-plan-heading">
                <div id="monthly-plan" class="job-option plan ${this.__selectedPlan === 'MONTHLY' ? 'selected' : ''}">
                  <div class="layout vertical flex">
                    <strong>Monthly</strong>
                    <span class="desc">Month-to-month flexibility</span>
                  </div>
                  <strong class="fee">
                    ${renderMoney(this.__pricing.monthly, 'GBP')}
                    <span>/m</span>
                  </strong>
                  ${this.__renderPlanButton('MONTHLY')}
                </div>
                <div id="yearly-plan" class="job-option plan ${this.__selectedPlan === 'YEARLY' ? 'selected' : ''}">
                  <div class="layout vertical flex">
                    <strong>Yearly</strong>
                    <span class="desc">Pay ${renderMoney(this.__pricing.yearly, 'GBP')} upfront and save 20%</span>
                  </div>
                  <strong class="fee">
                    ${renderMoney(this.__pricing.yearly / 12, 'GBP')}
                    <span>/m</span>
                  </strong>
                  ${this.__renderPlanButton('YEARLY')}
                </div>
              </div>
            `}
      </div>
    `;
  }

  private __renderBilling() {
    const billing = this.__billing;
    return html`
      ${when(
        billing && !this.__editing,
        () => html`
          <div id="created-customer" class="job-panel">
            <h3>Billing details</h3>
            <ns-summary-card
              heading=${this.__companyLabel}
              .rows=${[
                { label: 'Email', value: billing?.email },
                { label: 'Phone', value: billing?.phone },
                { label: 'Address line 1', value: billing?.address.address1 },
                { label: 'Address line 2', value: billing?.address.address2 },
                { label: 'Town or city', value: billing?.address.city },
                { label: 'Postcode', value: billing?.address.postcode },
                {
                  label: 'Country',
                  value: billing ? (COUNTRY_NAMES[billing.address.country] ?? billing.address.country) : undefined,
                },
              ]}
              ?confirmed=${this.__customerConfirmed || this.__succeeded === true}
              @confirm=${() => billing && this.__submitBilling(billing)}
            ></ns-summary-card>
          </div>
        `,
        () => html`
          <div class="job-panel">
            <form
              novalidate
              @submit=${(e: Event) => {
                e.preventDefault();
                void this.__confirmForm();
              }}
            >
              <ns-billing-details .prefill=${this.__prefill} ?disabled=${this.__submitting}>
                <p>Your company's subscription and its invoices are billed to these details.</p>
              </ns-billing-details>
            </form>
          </div>
        `,
      )}
    `;
  }

  private __renderPaymentMethod() {
    const paymentMethod = this.__subscription?.paymentMethods?.find((pm) => pm.active);
    return html`
      <div class="job-option">
        <span>${paymentMethod?.customerName}</span>
        <span class="flex">**** **** **** ${paymentMethod?.last4}</span>
        <div
          class="brand-icon"
          style="background-image:url(/images/icons/${paymentMethod?.brand}-logo.svg)"
          role="img"
          aria-label="${paymentMethod?.brand ?? 'Card'} logo"
        ></div>
      </div>
    `;
  }

  // The plan is fixed once details have been sent for it (payment/subscribe refuses another): Change details reopens only
  // the billing fieldset, as another plan would open a second subscription row the card never reloads
  private get __planLocked() {
    return !!this.__subscription || !!this.__billing || this.__customerConfirmed;
  }

  private __renderPlanButton(plan: 'MONTHLY' | 'YEARLY') {
    const selected = this.__selectedPlan === plan;
    return html`
      <wa-button
        size="s"
        type="button"
        variant=${selected ? 'brand' : 'success'}
        ?disabled=${this.__planLocked}
        @click=${() => this.__selectPaymentPlan(plan)}
      >
        ${selected ? 'Selected' : 'Select'}
      </wa-button>
    `;
  }

  render() {
    return html`
      ${this.__announcer.render()}
      <ns-job-layout>
        ${this.__renderSubscriptionPlan()} ${this.__company ? this.__renderBilling() : nothing}
        ${this.__clientSecret && this.__succeeded !== true
          ? html`
              <div class="job-panel">
                <slot name="stripe-payment-form"></slot>
              </div>
            `
          : nothing}
        ${this.__succeeded === true
          ? html`
              <div class="job-panel payment-result">
                <h3>Payment Method</h3>
                ${this.__renderPaymentMethod()}
                <div class="layout vertical center">
                  <img alt="" src="/images/icons/success.svg" />
                  <div>Payment Successful!</div>
                </div>
              </div>
            `
          : nothing}
        ${when(
          this.__succeeded === false && !this.__customerConfirmed,
          () => html`
            <div class="job-panel">
              <p>Your payment wasn't completed. Confirm the billing details to try again.</p>
            </div>
          `,
        )}
      </ns-job-layout>
    `;
  }

  renderActions() {
    if (this.__succeeded === true) {
      return html`
        <wa-button variant="brand" type="button" @click=${() => this.outcome('complete')}>Complete</wa-button>
      `;
    }
    if (!this.__company) return nothing;
    if (!this.__billing || this.__editing) {
      return html`
        <wa-button variant="brand" type="button" ?disabled=${this.__submitting} @click=${() => this.__confirmForm()}>
          Confirm
        </wa-button>
        ${this.__editing
          ? html`
              <wa-button
                variant="brand"
                appearance="outlined"
                type="button"
                ?disabled=${this.__submitting}
                @click=${() => this.__cancelChange()}
              >
                Cancel
              </wa-button>
            `
          : nothing}
      `;
    }
    const change = html`
      <wa-button
        variant="brand"
        appearance="outlined"
        type="button"
        ?disabled=${this.__submitting}
        @click=${() => this.__changeDetails()}
      >
        Change details
      </wa-button>
    `;
    if (this.__clientSecret) {
      return html`
        <wa-button variant="brand" type="button" @click=${() => this.__completePayment()}>Pay</wa-button>
        ${change}
      `;
    }
    return change;
  }
}
